import { useState } from "react";
import { X } from "lucide-react";
import { useResendVerificationEmail } from "../../hooks/control";

type ModalResendVerificationProps = {
	open: boolean;
	onClose: () => void;
	onEmailSent?: () => void;
};

export function ModalResendVerification({
	open,
	onClose,
	onEmailSent,
}: ModalResendVerificationProps) {
	const { resendVerificationEmail, loading: resendLoading } = useResendVerificationEmail();
	const [email, setEmail] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	if (!open) return null;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		const trimmed = email.trim();
		if (!trimmed) {
			setError("Ingresa tu correo electrónico");
			return;
		}

		try {
			const payload = await resendVerificationEmail(trimmed);
			const isSuccess = payload?.success === true;
			const message =
				typeof payload?.message === "string" && payload.message.trim()
					? payload.message
					: isSuccess
						? "Correo de verificación reenviado. Revisa tu bandeja de entrada."
						: "No se pudo reenviar el correo de verificación.";

			if (isSuccess) {
				setSuccess(true);
				onEmailSent?.();
			} else {
				setError(message);
			}
		} catch (err) {
			const msg =
				err instanceof Error
					? err.message
					: "No se pudo enviar el correo. Verifica el email e intenta de nuevo.";
			setError(msg);
		}
	};

	const handleClose = () => {
		setEmail("");
		setError(null);
		setSuccess(false);
		onClose();
	};

	return (
		<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4'>
			<div className='relative w-full max-w-sm rounded-2xl bg-white px-8 py-8 shadow-[0_16px_40px_rgba(15,23,42,0.12)]'>
				<button
					type='button'
					onClick={handleClose}
					aria-label='Cerrar'
					className='absolute right-4 top-4 cursor-pointer rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600'
				>
					<X className='h-4 w-4' />
				</button>

				{success ? (
					<div className='text-center'>
						<h2 className='text-xl font-medium text-slate-700'>
							Correo Enviado
						</h2>
						<p className='mt-3 text-sm text-slate-500'>
							Hemos enviado un enlace de verificación a{" "}
							<span className='font-medium text-slate-700'>{email}</span>.
							Revisa tu bandeja de entrada.
						</p>
						<button
							type='button'
							onClick={handleClose}
							className='mt-6 w-full cursor-pointer rounded-full bg-primary py-2.5 text-sm font-semibold text-white transition hover:bg-primary-hover'
						>
							Entendido
						</button>
					</div>
				) : (
					<>
						<h2 className='text-center text-xl font-medium text-slate-700'>
							Reenviar Verificación
						</h2>
						<p className='mt-3 text-center text-sm text-slate-500'>
							Ingresa tu correo electrónico asociado a tu cuenta para verificarla.
						</p>

						<form onSubmit={handleSubmit} className='mt-6 space-y-4'>
							{error ? (
								<div className='rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700'>
									{error}
								</div>
							) : null}

							<div className='space-y-2'>
								<label className='block text-sm font-medium text-slate-600'>
									Correo Electrónico
								</label>
								<input
									type='email'
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder='tu@email.com'
									className='w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
									autoComplete='email'
									required
								/>
							</div>

							<button
								type='submit'
								disabled={resendLoading}
								className='w-full cursor-pointer rounded-full bg-primary py-3 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60'
							>
								{resendLoading ? "Enviando..." : "Reenviar Verificación"}
							</button>
						</form>
					</>
				)}
			</div>
		</div>
	);
}
